import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { OtpGateProvider, useOtpGate } from './context/OtpGateContext';
import { ContactRevealProvider } from './context/ContactRevealContext';
import { SavedProvider } from './context/SavedContext';
import { CityProvider } from './context/CityContext';
import { CompareProvider, useCompare } from './context/CompareContext';
import { LazyToaster } from './components/LazyToaster';
import { getSubdomain, isArtistsSubdomain, isAdminSubdomain, isMainDomain } from './utils/subdomainUtils';
import { initializeAnalytics, initMetaPixel, captureAttribution, trackPageView, setUserProperties } from './utils/analytics';
import PageMeta from './components/PageMeta';
import { buildMeta } from './content/meta';
import { LocationProvider } from './context/LocationContext';
import { ScrollManager, RootRedirect, LegacyListingRedirect, LegacyArtistRedirect, TrailingSlashRedirect } from './components/routing';
import GlobalSeo from './components/GlobalSeo';
import { parseCitySegment, CATEGORY_SLUGS, pluralSegment, SINGULAR_TYPE } from './config/geo';
import PrefSignals from './components/PrefSignals';

// Loading Spinner Component
// data-page-loader: index.html keeps its header + card skeleton up while this shows.
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50" data-page-loader>
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-[#FF1D25] border-t-transparent rounded-full animate-spin mx-auto" />
      <p className="mt-4 text-gray-500 text-sm">Loading...</p>
    </div>
  </div>
);

// Lazy load pages for code splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const ArtistListingPage = lazy(() => import('./pages/ArtistListingPage'));
const ArtistProfilePage = lazy(() => import('./pages/ArtistProfilePage'));
const ComparePage = lazy(() => import('./pages/ComparePage'));
const HostLogin = lazy(() => import('./pages/HostLogin'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
const MyRequestsPage = lazy(() => import('./pages/MyRequestsPage'));
const TeamLogin = lazy(() => import('./pages/TeamLogin'));
const ArtistLogin = lazy(() => import('./pages/ArtistLogin'));
const CustomerDashboard = lazy(() => import('./pages/CustomerDashboard'));
const ArtistDashboard = lazy(() => import('./pages/ArtistDashboard'));
const ArtistPendingApprovalPage = lazy(() => import('./pages/ArtistPendingApprovalPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const OccasionMapAdmin = lazy(() => import('./pages/OccasionMapAdmin'));
const AdminListingContent = lazy(() => import('./pages/AdminListingContent'));
const AdminReveals = lazy(() => import('./pages/AdminReveals'));
const AdminReviews = lazy(() => import('./pages/AdminReviews'));
const ReviewPage = lazy(() => import('./pages/ReviewPage'));
const AdminCollections = lazy(() => import('./pages/AdminCollections'));
const AdminNudges = lazy(() => import('./pages/AdminNudges'));
const AdminMessages = lazy(() => import('./pages/AdminMessages'));
const NotFound = lazy(() => import('./pages/NotFound'));
const AdminApprovals = lazy(() => import('./pages/AdminApprovals'));
const StyleGuidePage = lazy(() => import('./pages/StyleGuidePage'));
const SavedPage = lazy(() => import('./pages/SavedPage'));
const CollectionPage = lazy(() => import('./pages/CollectionPage'));
const PostRequirementPage = lazy(() => import('./pages/PostRequirementPage'));
const ArtistSignupWizard = lazy(() => import('./pages/ArtistSignup/ArtistSignupWizard'));
const ArtistEditorPage = lazy(() => import('./pages/ArtistEditor/ArtistEditorPage'));
const ArtistHome = lazy(() => import('./pages/artist/ArtistHome'));
const ArtistRanking = lazy(() => import('./pages/artist/ArtistRanking'));
const ArtistShare = lazy(() => import('./pages/artist/ArtistShare'));
const ArtistRequests = lazy(() => import('./pages/artist/ArtistRequests'));
const ArtistRequestPage = lazy(() => import('./pages/artist/ArtistRequestPage'));
const AdminRequirements = lazy(() => import('./pages/AdminRequirements'));
const AdminCustomer = lazy(() => import('./pages/AdminCustomer'));
const ListAsArtist = lazy(() => import('./pages/artist/ListAsArtist'));
const AdminTeam = lazy(() => import('./pages/AdminTeam'));
const AdminSEO = lazy(() => import('./pages/AdminSEO'));
const ArtistShareRedirect = lazy(() => import('./pages/artist/ArtistShareRedirect'));
const PriceGuidePage = lazy(() => import('./pages/PriceGuidePage'));
const AdminPriceGuide = lazy(() => import('./pages/AdminPriceGuide'));
const AdminSearchTerms = lazy(() => import('./pages/AdminSearchTerms'));
const AdminDemandGaps = lazy(() => import('./pages/AdminDemandGaps'));
const AdminWebVitals = lazy(() => import('./pages/AdminWebVitals'));
// P27 — lazy so the WhatsApp help button never ships in the initial bundle.
const FloatingWhatsAppButton = lazy(() => import('./components/FloatingWhatsAppButton'));
// R6 — off the main bundle: placeholder page, compare bar (only with items).
const ComingSoon = lazy(() => import('./components/ComingSoon'));
const CompareBar = lazy(() => import('./components/CompareBar'));

// Artist Route Guard - Blocks ALL artists from accessing public site
const ArtistRouteGuard = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  
  // If user is an artist, redirect based on their profile status
  if (isAuthenticated && user?.role === 'artist') {
    // Pending approval - show waiting screen
    if (user?.artist_profile_status === 'pending_approval') {
      return <Navigate to="/artist-pending" replace />;
    }
    // Rejected or changes requested - go to the profile editor
    if (user?.artist_profile_status === 'rejected' || user?.artist_profile_status === 'changes_requested') {
      return <Navigate to="/artist/profile" replace />;
    }
    // Draft status - go to the profile editor
    if (user?.artist_profile_status === 'draft' || !user?.artist_profile_status) {
      return <Navigate to="/artist/profile" replace />;
    }
    // Approved artists - redirect to dashboard (block from browsing site)
    return <Navigate to="/artist" replace />;
  }
  
  return children;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, loading, authChecked } = useAuth();
  const location = useLocation();

  // Cookie sessions have no stored token, so wait for /auth/me before deciding.
  if (loading || !authChecked) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/team/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Customer Gate - opens the phone-OTP sheet instead of redirecting to a login page.
const CustomerGate = ({ children }) => {
  const { isAuthenticated, user, authChecked } = useAuth();
  const { requireCustomer } = useOtpGate();
  const opened = useRef(false);

  useEffect(() => {
    if (!authChecked) return; // wait for the /me cookie check to resolve first
    if (isAuthenticated) {
      opened.current = false;
    } else if (!opened.current) {
      opened.current = true;
      requireCustomer({ context: 'confirm', onSuccess: () => { opened.current = false; } });
    }
  }, [authChecked, isAuthenticated, requireCustomer]);

  if (!authChecked) return <PageLoader />;
  if (isAuthenticated && user?.role === 'customer') return children;
  if (isAuthenticated) return <Navigate to="/" replace />;
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6 text-center" data-testid="customer-gate-prompt">
      <p className="text-gray-500">Verify your WhatsApp number to see your dashboard.</p>
    </div>
  );
};
const ArtistProtectedRoute = ({ children }) => {
  const { isAuthenticated, user, loading, authChecked } = useAuth();
  const location = useLocation();

  if (loading || !authChecked) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/artist/login" state={{ from: location }} replace />;
  }

  if (user?.role !== 'artist') {
    return <Navigate to="/" replace />;
  }

  // Check artist profile status - redirect non-approved artists appropriately
  // BUT only if we're not already on the profile setup or pending page
  const profileStatus = user?.artist_profile_status;
  const currentPath = location.pathname;
  
  // Skip redirect checks if already on the target pages
  if (currentPath === '/artist-profile-setup' || currentPath === '/artist-pending' || currentPath === '/artist/profile') {
    return children;
  }
  
  // Pending approval - show waiting screen
  if (profileStatus === 'pending_approval') {
    return <Navigate to="/artist-pending" replace />;
  }
  
  // Draft, rejected, or changes requested - go to the profile editor
  if (profileStatus === 'draft' || profileStatus === 'rejected' || profileStatus === 'changes_requested' || !profileStatus) {
    return <Navigate to="/artist/profile" replace />;
  }

  return children;
};

// P18: /artist-profile-setup is retired. Send artists to the new editor, visitors
// to signup. We never render the old 8k-line setup page any more.
const LegacySetupRedirect = () => {
  const { isAuthenticated, user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (isAuthenticated && user?.role === 'artist') return <Navigate to="/artist/profile" replace />;
  return <Navigate to="/artist/signup" replace />;
};

// Subdomain Route Handler - Disabled to allow all access on main domain
// Subdomain-specific routing only activates when actually on a subdomain
const SubdomainHandler = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return <PageLoader />;
  }
  
  // Subdomain restrictions disabled - all routes accessible on main domain
  // These checks only apply if you're actually on a subdomain (artists.* or admin.*)
  
  // Handle artists.artistscanner.ai subdomain (only if actually on artists subdomain)
  if (isArtistsSubdomain()) {
    if (location.pathname === '/') {
      if (isAuthenticated && user?.role === 'artist') {
        if (user?.artist_profile_status === 'approved') {
          return <Navigate to="/artist" replace />;
        }
        if (user?.artist_profile_status === 'pending_approval') {
          return <Navigate to="/artist-pending" replace />;
        }
        return <Navigate to="/artist/profile" replace />;
      }
      return <Navigate to="/artist/signup" replace />;
    }
  }
  
  // Handle admin.artistscanner.ai subdomain (only if actually on admin subdomain)
  if (isAdminSubdomain()) {
    if (location.pathname === '/') {
      if (isAuthenticated && (user?.role === 'admin' || user?.role === 'management')) {
        return <Navigate to="/admin" replace />;
      }
      return <Navigate to="/team/login" replace />;
    }
    
    const allowedPaths = ['/team/login', '/admin'];
    const isAllowed = allowedPaths.some(path => location.pathname.startsWith(path));
    if (!isAllowed) {
      return <Navigate to="/admin" replace />;
    }
  }
  
  return children;
};

// Admin Subdomain Guard - Disabled to allow admin access on main domain
// Previously redirected admins to admin subdomain, now allows access everywhere
const AdminSubdomainGuard = ({ children }) => {
  // Subdomain restriction disabled - admins can access from any domain
  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <SubdomainHandler>
        <Routes>
          {/* Legacy redirects (client-side; server 301s come later) */}
          <Route path="/" element={<RootRedirect />} />
          <Route path="/artists" element={<LegacyListingRedirect />} />
          <Route path="/artists/:id" element={<LegacyArtistRedirect />} />

          {/* City hub + browse + occasion/category/area (all delhi-ncr for now) */}
          <Route path="/delhi-ncr" element={<ArtistRouteGuard><HomePage /></ArtistRouteGuard>} />
          <Route path="/delhi-ncr/artists" element={<ArtistRouteGuard><ArtistListingPage /></ArtistRouteGuard>} />
          <Route path="/delhi-ncr/c/:slug" element={<ArtistRouteGuard><CollectionPage /></ArtistRouteGuard>} />
          <Route path="/delhi-ncr/price-guide/:category" element={<ArtistRouteGuard><PriceGuidePage /></ArtistRouteGuard>} />
          <Route path="/delhi-ncr/:occasion/:category" element={<ArtistRouteGuard><CityOccasionCategoryRoute /></ArtistRouteGuard>} />
          <Route path="/delhi-ncr/:seg" element={<ArtistRouteGuard><CitySegmentRoute /></ArtistRouteGuard>} />

          {/* Legacy categories page kept working */}
          <Route path="/categories" element={<ArtistRouteGuard><ArtistListingPage /></ArtistRouteGuard>} />
          <Route path="/compare" element={<ArtistRouteGuard><PageMeta meta={buildMeta('search')} /><ComparePage /></ArtistRouteGuard>} />
          <Route path="/request-quote" element={<Navigate to="/post" replace />} />

          {/* New top-level routes */}
          <Route path="/saved" element={<ArtistRouteGuard><SavedPage /></ArtistRouteGuard>} />
          <Route path="/post" element={<ArtistRouteGuard><PostRequirementPage /></ArtistRouteGuard>} />
          <Route path="/review/:token" element={<ReviewPage />} />
          <Route path="/list-as-artist" element={<ListAsArtist />} />
          <Route path="/my-requests" element={<MyRequestsPage />} />

          {/* R2 — interim legal / info pages (copy: content/legal_interim.md) */}
          <Route path="/about" element={<LegalPage slug="about" />} />
          <Route path="/contact" element={<LegalPage slug="contact" />} />
          <Route path="/privacy" element={<LegalPage slug="privacy" />} />
          <Route path="/terms" element={<LegalPage slug="terms" />} />

          {/* P22 — artist share link: record attribution, then open the profile */}
          <Route path="/a/:slug" element={<ArtistShareRedirect />} />

          {/* Artist public profile (slug) + nested */}
          <Route path="/artist/signup" element={<ArtistSignupWizard />} />
          <Route path="/artist" element={<ArtistProtectedRoute><ArtistHome /></ArtistProtectedRoute>} />
          <Route path="/artist/profile" element={<ArtistEditorPage />} />
          <Route path="/artist/verify" element={<ComingSoon title="Verify your ID — coming soon" metaTitle="Verify your ID" />} />
          <Route path="/artist/ranking" element={<ArtistProtectedRoute><ArtistRanking /></ArtistProtectedRoute>} />
          <Route path="/artist/share" element={<ArtistProtectedRoute><ArtistShare /></ArtistProtectedRoute>} />
          <Route path="/artist/requests" element={<ArtistProtectedRoute><ArtistRequests /></ArtistProtectedRoute>} />
          <Route path="/artist/requests/:id" element={<ArtistProtectedRoute><ArtistRequestPage /></ArtistProtectedRoute>} />
          {/* Single route (splat) so switching Overview/Reviews/Photos does NOT remount the page */}
          <Route path="/artist/:slug/*" element={<ArtistRouteGuard><ArtistProfilePage /></ArtistRouteGuard>} />

          {/* Auth routes */}
          {/* R2 — host phone-OTP login (the OTP sheet as a page) */}
          <Route path="/login" element={<HostLogin />} />
          {/* F4 — private staff sign-in (never linked, noindex) */}
          <Route path="/team/login" element={<TeamLogin />} />
          {/* F4 — artist WhatsApp sign-in */}
          <Route path="/artist/login" element={<ArtistLogin />} />
          {/* F4 — legacy /admin/login now points at the private team sign-in */}
          <Route path="/admin/login" element={<Navigate to="/team/login" replace />} />
          {/* R2 — no passwords: old register / reset links land on the OTP logins */}
          <Route path="/register" element={<Navigate to="/artist/signup" replace />} />
          <Route path="/signup/artist" element={<Navigate to="/artist/signup" replace />} />
          <Route path="/forgot-password" element={<Navigate to="/login" replace />} />
          <Route path="/reset-password" element={<Navigate to="/login" replace />} />

          {/* Customer routes */}
          <Route
            path="/dashboard"
            element={
              <CustomerGate>
                <PageMeta meta={buildMeta('saved', { title: 'Your account' })} />
                <CustomerDashboard />
              </CustomerGate>
            }
          />

          {/* Artist routes */}
          <Route
            path="/artist-dashboard"
            element={
              <ArtistProtectedRoute>
                <PageMeta meta={buildMeta('dashboard', { title: 'Artist dashboard' })} />
                <ArtistDashboard />
              </ArtistProtectedRoute>
            }
          />
          <Route
            path="/artist/preview/:id"
            element={
              <ArtistProtectedRoute>
                <ArtistProfilePage isPreview={true} />
              </ArtistProtectedRoute>
            }
          />
          <Route
            path="/artist-profile-setup"
            element={<LegacySetupRedirect />}
          />
          <Route
            path="/artist-pending"
            element={
              <ArtistProtectedRoute>
                <PageMeta meta={buildMeta('dashboard', { title: 'Pending approval' })} />
                <ArtistPendingApprovalPage />
              </ArtistProtectedRoute>
            }
          />

          {/* Admin routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={['admin', 'management', 'designer', 'artist_manager']}>
                <PageMeta meta={buildMeta('dashboard', { title: 'Admin' })} />
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/occasion-map"
            element={
              <ProtectedRoute allowedRoles={['admin', 'management']}>
                <PageMeta meta={buildMeta('dashboard', { title: 'Occasion map' })} />
                <OccasionMapAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/listing-content"
            element={
              <ProtectedRoute allowedRoles={['admin', 'management']}>
                <PageMeta meta={buildMeta('dashboard', { title: 'Listing content' })} />
                <AdminListingContent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/price-guides"
            element={
              <ProtectedRoute allowedRoles={['admin', 'management']}>
                <PageMeta meta={buildMeta('dashboard', { title: 'Price guides' })} />
                <AdminPriceGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/search-terms"
            element={
              <ProtectedRoute allowedRoles={['admin', 'management']}>
                <PageMeta meta={buildMeta('dashboard', { title: 'Search terms' })} />
                <AdminSearchTerms />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/requirements"
            element={
              <ProtectedRoute allowedRoles={['admin', 'management', 'artist_manager']}>
                <PageMeta meta={buildMeta('dashboard', { title: 'Requirements' })} />
                <AdminRequirements />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/demand-gaps"
            element={
              <ProtectedRoute allowedRoles={['admin', 'management']}>
                <PageMeta meta={buildMeta('dashboard', { title: 'Demand gaps' })} />
                <AdminDemandGaps />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/web-vitals"
            element={
              <ProtectedRoute allowedRoles={['admin', 'management']}>
                <PageMeta meta={buildMeta('dashboard', { title: 'Web Vitals' })} />
                <AdminWebVitals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reveals"
            element={
              <ProtectedRoute allowedRoles={['admin', 'management', 'artist_manager']}>
                <PageMeta meta={buildMeta('dashboard', { title: 'Contact reveals' })} />
                <AdminReveals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/customers/:customerId"
            element={
              <ProtectedRoute allowedRoles={['admin', 'management', 'artist_manager']}>
                <PageMeta meta={buildMeta('dashboard', { title: 'Customer' })} />
                <AdminCustomer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reviews"
            element={
              <ProtectedRoute allowedRoles={['admin', 'management']}>
                <PageMeta meta={buildMeta('dashboard', { title: 'Reviews moderation' })} />
                <AdminReviews />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/collections"
            element={
              <ProtectedRoute allowedRoles={['admin', 'management']}>
                <PageMeta meta={buildMeta('dashboard', { title: 'Collections' })} />
                <AdminCollections />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/nudges"
            element={
              <ProtectedRoute allowedRoles={['admin', 'management']}>
                <PageMeta meta={buildMeta('dashboard', { title: 'Profile nudges' })} />
                <AdminNudges />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/messages"
            element={
              <ProtectedRoute allowedRoles={['admin', 'management']}>
                <PageMeta meta={buildMeta('dashboard', { title: 'WhatsApp messages' })} />
                <AdminMessages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/approvals"
            element={
              <ProtectedRoute allowedRoles={['admin', 'management', 'designer', 'artist_manager']}>
                <PageMeta meta={buildMeta('dashboard', { title: 'Approvals' })} />
                <AdminApprovals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/team"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <PageMeta meta={buildMeta('dashboard', { title: 'Team & Roles' })} />
                <AdminTeam />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute allowedRoles={['admin', 'management']}>
                <PageMeta meta={buildMeta('dashboard', { title: 'SEO settings' })} />
                <AdminSEO />
              </ProtectedRoute>
            }
          />
          <Route
            path="/styleguide"
            element={
              <ProtectedRoute allowedRoles={['admin', 'management']}>
                <PageMeta meta={buildMeta('dashboard', { title: 'Style guide' })} />
                <StyleGuidePage />
              </ProtectedRoute>
            }
          />

          {/* Catch all - redirect to home */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </SubdomainHandler>
    </Suspense>
  );
}

// P9 — /delhi-ncr/:seg (occasion | category | occasion-in-area | category-in-area)
function CitySegmentRoute() {
  const { seg } = useParams();
  const { search, hash } = useLocation();
  const plural = pluralSegment(seg);
  if (plural) return <Navigate to={`/delhi-ncr/${plural}${search}${hash}`} replace />;
  return <ArtistListingPage pathContext={parseCitySegment(seg)} />;
}

// P9 — /delhi-ncr/:occasion/:category
function CityOccasionCategoryRoute() {
  const { occasion, category } = useParams();
  const { search, hash } = useLocation();
  if (SINGULAR_TYPE[category]) return <Navigate to={`/delhi-ncr/${occasion}/${SINGULAR_TYPE[category]}${search}${hash}`} replace />;
  return (
    <ArtistListingPage
      pathContext={{ occasionSlug: occasion, category: CATEGORY_SLUGS[category], categorySlug: category }}
    />
  );
}

function App() {
  // Initialize analytics on app load
  useEffect(() => {
    initializeAnalytics();
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <CityProvider>
          <LocationProvider>
            <CompareProvider>
              <OtpGateProvider>
                <ContactRevealProvider>
                  <SavedProvider>
                    <ScrollManager />
                    <TrailingSlashRedirect />
                    <PrefSignals />
                    <GlobalSeo />
                    <AnalyticsTracker />
                    <AppRoutes />
                    <LazyCompareBar />
                    <WhatsAppHelpButton />
                    <LazyToaster position="top-right" richColors />
                  </SavedProvider>
                </ContactRevealProvider>
              </OtpGateProvider>
            </CompareProvider>
          </LocationProvider>
        </CityProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

// Analytics tracker component for page views and user properties
function AnalyticsTracker() {
  const location = useLocation();
  const { user, authChecked } = useAuth();

  // Capture first/last touch attribution on first load and every route change.
  useEffect(() => {
    captureAttribution();
  }, [location.pathname, location.search]);

  // Mark internal (admin/team) sessions before any hit fires.
  useEffect(() => {
    if (user) setUserProperties(user);
  }, [user]);

  // Load the Meta Pixel only once auth is known (never for admin/team sessions).
  useEffect(() => {
    if (authChecked) initMetaPixel(user);
  }, [authChecked, user]);

  // Exactly one page_view per route change, fired AFTER the page sets its own
  // title. We wait for the <title> to change (lazy pages mount async), with a
  // short fallback so routes that keep the same title still report.
  useEffect(() => {
    if (!authChecked) return undefined;
    const path = location.pathname + location.search;
    let fired = false;
    const fire = () => {
      if (fired) return;
      fired = true;
      trackPageView(path, document.title);
    };
    let obs;
    const titleEl = document.querySelector('title');
    if (titleEl && typeof MutationObserver !== 'undefined') {
      obs = new MutationObserver(() => requestAnimationFrame(fire));
      obs.observe(titleEl, { childList: true, characterData: true, subtree: true });
    }
    const t = setTimeout(fire, 800);
    return () => { clearTimeout(t); if (obs) obs.disconnect(); };
  }, [location.pathname, location.search, authChecked]);

  return null;
}

// R6 — the compare bar's code loads only once something is in the compare list.
function LazyCompareBar() {
  const { compareCount } = useCompare();
  if (!compareCount) return null;
  return (
    <Suspense fallback={null}>
      <CompareBar />
    </Suspense>
  );
}

// WhatsApp Help Button - Only shows on customer-facing pages
function WhatsAppHelpButton() {
  const location = useLocation();
  const { user } = useAuth();
  
  // Hide on admin pages
  const adminPaths = ['/admin'];
  const isAdminPage = adminPaths.some(path => location.pathname.startsWith(path));
  
  // Hide for admin users
  const isAdminUser = user?.role === 'admin' || user?.role === 'management';
  
  // Hide on artist dashboard, setup, signup wizard and profile editor pages
  const artistPaths = ['/artist-dashboard', '/artist-profile-setup', '/artist-pending', '/artist/signup', '/artist/profile', '/artist/ranking', '/artist/share'];
  const isArtistPage = artistPaths.some(path => location.pathname.startsWith(path)) || location.pathname === '/artist';
  
  // Show only on customer-facing pages
  if (isAdminPage || isAdminUser || isArtistPage) {
    return null;
  }
  
  return (
    <Suspense fallback={null}>
      <FloatingWhatsAppButton />
    </Suspense>
  );
}

export default App;
